import { useState } from "react";
import { Button } from "@/components/ui/button";
import { lovable } from "@/integrations/lovable/index";
import { toast } from "@/hooks/use-toast";
import { useI18n } from "@/lib/i18n";

interface SocialLoginButtonsProps {
  loading?: boolean;
}

const SocialLoginButtons = ({ loading }: SocialLoginButtonsProps) => {
  const [socialLoading, setSocialLoading] = useState<"google" | "apple" | null>(null);
  const { t } = useI18n();

  const handleSocialLogin = async (provider: "google" | "apple") => {
    setSocialLoading(provider);
    try {
      const result = await lovable.auth.signInWithOAuth(provider, {
        redirect_uri: window.location.origin,
      });
      if (result.error) {
        toast({
          title: t("social.loginFailed"),
          description: result.error instanceof Error ? result.error.message : String(result.error),
          variant: "destructive",
        });
      }
    } catch (err: any) {
      toast({ title: t("social.loginFailed"), description: err?.message || t("social.unknownError"), variant: "destructive" });
    } finally {
      setSocialLoading(null);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <Button
        type="button"
        variant="outline"
        size="lg"
        className="w-full rounded-xl font-medium gap-3 h-12"
        disabled={!!socialLoading || loading}
        onClick={() => handleSocialLogin("google")}
      >
        {socialLoading === "google" ? (
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : (
          <svg className="h-5 w-5" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
          </svg>
        )}
        {t("social.continueGoogle")}
      </Button>

      <Button
        type="button"
        variant="outline"
        size="lg"
        className="w-full rounded-xl font-medium gap-3 h-12"
        disabled={!!socialLoading || loading}
        onClick={() => handleSocialLogin("apple")}
      >
        {socialLoading === "apple" ? (
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : (
          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C4.24 16.7 4.89 10.87 8.7 10.64c1.27.07 2.15.72 2.9.78.88-.18 1.73-.87 2.93-.74 1.24.12 2.17.64 2.78 1.57-2.51 1.46-1.95 4.55.38 5.52-.54 1.34-.95 2.2-1.64 3.51zM12.03 10.6C11.87 8.46 13.57 6.69 15.6 6.5c.29 2.36-2.03 4.25-3.57 4.1z" />
          </svg>
        )}
        {t("social.continueApple")}
      </Button>
    </div>
  );
};

export default SocialLoginButtons;
